import { termosDeBusca } from "./sinonimos";
import type { CrestPiece, CrestShapeData } from "@legacy-manager/engine";

/**
 * O catálogo de figuras: quem carrega, quem busca e quem transforma em peça.
 *
 * **Os dois arquivos ficam fora do pacote principal.** Somados dão 1,5 MB, e
 * quem abre o jogo para jogar a rodada nunca vai desenhar escudo nenhum: o
 * catálogo só interessa na criação do clube, que acontece uma vez por carreira.
 * Por isso a leitura é `import()` dinâmico, um por aba, e o empacotador põe cada
 * um no seu pedaço. Carregar os dois na abertura custaria a espera do catálogo
 * em toda partida.
 *
 * **O `?raw` não é enfeite.** Importar o JSON direto faz o compilador inferir o
 * tipo inteiro do arquivo, com as 1103 chaves e cada `d` de caminho virando tipo
 * literal, e o `tsc --noEmit` do projeto passa a levar minutos. Lido como texto,
 * o compilador vê uma `string` e nada mais, e a forma do dado é o
 * `CatalogoBruto` declarado aqui, afirmado na hora do `JSON.parse`. O dado é
 * gerado por `scripts/gerar-catalogo-de-brasoes.mjs`, deste mesmo repositório,
 * então a afirmação é sobre um arquivo nosso e não sobre entrada de fora.
 *
 * **Nada aqui escreve marcação.** As formas atravessam este arquivo como dado e
 * chegam a quem desenha do mesmo jeito, pelo motivo que `Escudo.tsx` explica: no
 * online o escudo do outro chega pela rede.
 */

/** Os dois traços do catálogo. Cada um é um arquivo, uma grade e uma aba. */
export type EstiloDeFigura = "cheios" | "contornos";

/**
 * As sete categorias, na ordem em que o filtro as oferece.
 *
 * É a ordem alfabética do português, que é a do dicionário, e não a da
 * quantidade de figuras: quem procura "natureza" procura pela palavra, e uma
 * lista ordenada por tamanho muda de ordem a cada figura nova no catálogo.
 */
export const CATEGORIAS = [
  "animais",
  "armas",
  "esporte",
  "heraldica",
  "maquina",
  "natureza",
  "simbolos",
] as const;

export type CategoriaDeFigura = (typeof CATEGORIAS)[number];

/** Uma figura do catálogo, já pronta para desenhar e para comparar na busca. */
export interface Figura {
  /** Como `g:eagle-emblem`. É o que a peça guarda para o crédito saber a origem. */
  id: string;
  /** O nome legível, que vira o rótulo acessível do botão da grade. */
  nome: string;
  categoria: CategoriaDeFigura;
  formas: CrestShapeData[];
  /**
   * O nome pronto para comparar: minúsculo, sem acento e sem hífen.
   *
   * Fica calculado de uma vez na leitura porque a busca roda a cada tecla sobre
   * 1103 figuras, e normalizar tudo dentro do laço faria 1103 `normalize` por
   * letra digitada.
   */
  termo: string;
}

/** Um catálogo inteiro, do jeito que a tela precisa dele. */
export interface CatalogoDeFiguras {
  estilo: EstiloDeFigura;
  /** 512 nas figuras cheias e 24 nos contornos. Vai para `grid` da peça. */
  grade: number;
  /** Contorno pede traço em vez de preenchimento. Vai para `outline` da peça. */
  contorno: boolean;
  /** Na ordem alfabética do id, que é como o gerador escreveu o arquivo. */
  figuras: Figura[];
}

/** A forma do arquivo, afirmada no `JSON.parse`. Ver o cabeçalho. */
interface CatalogoBruto {
  grade: number;
  tracejado: boolean;
  figuras: Record<string, { c: CategoriaDeFigura; f: CrestShapeData[] }>;
}

/**
 * Tamanho com que a peça nasce, e o piso que ela não pode furar.
 *
 * O piso é de legibilidade, não de gosto: na tabela o escudo tem 20 pixels de
 * lado, e uma peça de 24 ocupa ali menos de cinco. Abaixo disso o desenho deixa
 * de ser uma figura e vira um pontinho, e a pessoa passa a escolher uma coisa
 * que só ela, na prévia grande, consegue ver.
 */
export const TAMANHO_MINIMO = 24;
export const TAMANHO_PADRAO = 52;
export const TAMANHO_MAXIMO = 100;

/**
 * Onde a peça nova aparece e com que cor.
 *
 * Um pouco acima do meio da grade, que é onde o emblema fica em escudo de
 * verdade: centrado em 50 ele encosta na ponta de baixo da flâmula e do losango,
 * que afinam justamente ali. A cor é branca porque o fundo do escudo nasce
 * escuro, e quem recebe a peça troca pela cor escolhida na paleta.
 */
const POSICAO_INICIAL = { x: 50, y: 46 } as const;
const COR_INICIAL = "#ffffff";

/**
 * Minúsculo, sem acento e só com letra, número e espaço.
 *
 * O hífen vira espaço porque o nome da figura vem do id (`eagle-emblem`), e quem
 * digita escreve "eagle emblem". Sem esta troca, buscar por duas palavras
 * separadas não acharia nada.
 *
 * O acento cai por causa de quem digita, e não do catálogo: nome nenhum das 1240
 * figuras tem acento, mas o teclado brasileiro tem tecla morta, e um "lión"
 * digitado sem querer devolveria lista vazia em vez de "lion".
 */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** O que já está na memória, e o que ainda está voando. */
const prontos = new Map<EstiloDeFigura, CatalogoDeFiguras>();
const emVoo = new Map<EstiloDeFigura, Promise<CatalogoDeFiguras>>();

async function lerCatalogo(estilo: EstiloDeFigura): Promise<CatalogoDeFiguras> {
  /*
   * Os dois `import()` estão escritos por extenso, e não montados a partir do
   * estilo, porque o empacotador precisa enxergar o caminho para criar o pedaço
   * separado. Caminho montado em tempo de execução vira um pedido que só existe
   * em desenvolvimento e some na build.
   */
  const texto =
    estilo === "cheios"
      ? (await import("./cheios.json?raw")).default
      : (await import("./contornos.json?raw")).default;

  const bruto = JSON.parse(texto) as CatalogoBruto;

  const figuras = Object.entries(bruto.figuras).map(([id, dado]): Figura => {
    // O prefixo diz o catálogo de origem (`g:` e `l:`) e não é nome de nada.
    const nome = id.slice(id.indexOf(":") + 1).replace(/-/g, " ");
    return { id, nome, categoria: dado.c, formas: dado.f, termo: normalizar(nome) };
  });

  return { estilo, grade: bruto.grade, contorno: bruto.tracejado, figuras };
}

/**
 * O catálogo de um estilo, buscado uma vez só.
 *
 * O que fica guardado é o resultado e, enquanto ele não chega, a promessa. Sem
 * guardar a promessa, duas aberturas seguidas da mesma aba baixariam o pedaço
 * duas vezes, porque a segunda começaria antes de a primeira terminar. Falha
 * limpa a promessa e deixa a próxima tentativa acontecer: o arquivo pode ter
 * caído no meio do caminho, e o jeito de insistir é abrir a aba de novo.
 */
export function carregarCatalogo(estilo: EstiloDeFigura): Promise<CatalogoDeFiguras> {
  const pronto = prontos.get(estilo);
  if (pronto) return Promise.resolve(pronto);

  const andando = emVoo.get(estilo);
  if (andando) return andando;

  const promessa = lerCatalogo(estilo)
    .then((catalogo) => {
      prontos.set(estilo, catalogo);
      return catalogo;
    })
    .finally(() => {
      emVoo.delete(estilo);
    });

  emVoo.set(estilo, promessa);
  return promessa;
}

/**
 * O catálogo que já está na memória, sem esperar nada.
 *
 * Existe para a tela desenhar a aba de volta sem piscar vazia: trocar de aba e
 * voltar não pode mostrar um quadro em branco só porque a resposta vem por
 * promessa. Devolve `null` quando aquele estilo ainda não foi carregado.
 */
export function catalogoEmMemoria(estilo: EstiloDeFigura): CatalogoDeFiguras | null {
  return prontos.get(estilo) ?? null;
}

/**
 * A busca da grade: texto, categoria e uma ordem que ajuda.
 *
 * Todo termo digitado precisa aparecer, em qualquer ordem, e o casamento é por
 * pedaço: "drag" acha "dragon head" e "dragon balls". Com 1240 figuras, achar
 * cinquenta que servem é fácil; difícil é a que interessa estar entre as
 * primeiras, e por isso o resultado sai pesado. Começo do nome vem antes de
 * começo de palavra, que vem antes de meio de palavra: quem digita "eagle"
 * quer "eagle emblem" acima de "bald eagle" e de "beagle".
 *
 * A ordenação é estável, então figuras do mesmo peso continuam em ordem
 * alfabética, que é como o arquivo foi escrito.
 */
export function buscarFiguras(
  catalogo: CatalogoDeFiguras,
  texto: string,
  categoria: CategoriaDeFigura | null,
): Figura[] {
  const base =
    categoria === null
      ? catalogo.figuras
      : catalogo.figuras.filter((figura) => figura.categoria === categoria);

  const termos = normalizar(texto).split(" ").filter((termo) => termo !== "");
  if (termos.length === 0) return base;

  const achados: { figura: Figura; peso: number }[] = [];

  /*
   * Cada palavra digitada vira a lista do que ela pode querer dizer.
   *
   * Os nomes das figuras são em inglês, porque é assim que os dois pacotes de
   * origem as nomeiam, e o jogo abre em português. Sem esta tradução, quem
   * digita "águia" recebe "Nada encontrado." num catálogo com dezenove águias,
   * e a busca é o único caminho até as mil e cem figuras.
   *
   * A tradução é do que se procura, e não do que existe: `sinonimos.ts`
   * explica por quê.
   */
  const alternativas = termos.map((termo) => termosDeBusca(termo));

  for (const figura of base) {
    let peso = 0;
    let serve = true;

    for (const opcoes of alternativas) {
      /*
       * Basta uma das traduções casar, e vale a de menor peso.
       *
       * "águia" vira ["aguia", "eagle", "hawk", "falcon"], e a figura
       * `eagle-emblem` casa em "eagle" no começo do nome. Ficar com o menor
       * peso mantém a ordenação de sempre: quem casa no começo da palavra
       * aparece antes de quem casa no meio dela.
       */
      let melhor = -1;
      for (const opcao of opcoes) {
        const onde = figura.termo.indexOf(opcao);
        if (onde < 0) continue;
        const custo = onde === 0 ? 0 : figura.termo[onde - 1] === " " ? 1 : 2;
        if (melhor < 0 || custo < melhor) melhor = custo;
      }

      if (melhor < 0) {
        serve = false;
        break;
      }
      peso += melhor;
    }

    if (serve) achados.push({ figura, peso });
  }

  achados.sort((um, outro) => um.peso - outro.peso);
  return achados.map((achado) => achado.figura);
}

/**
 * Uma peça pronta a partir do id da figura.
 *
 * Sai com `draw`, `grid` e `outline` preenchidos, que é o que faz o escudo
 * desenhar sem consultar o catálogo de novo: a peça carrega o próprio desenho,
 * pelo motivo que o tipo do motor explica. Quem recebe muda posição, giro,
 * tamanho e cor à vontade.
 *
 * A lista de formas é copiada, e não emprestada. Duas peças da mesma figura
 * dividiriam o mesmo vetor, e o que uma delas guardasse no save apareceria na
 * outra: custa um vetor de meia dúzia de itens por clique e fecha a porta.
 *
 * Devolve `null` quando o id não é daquele catálogo, que é o que acontece ao
 * pedir uma figura cheia com a aba de contornos aberta.
 */
export function pecaDaFigura(
  catalogo: CatalogoDeFiguras,
  id: string,
  cor: string = COR_INICIAL,
): CrestPiece | null {
  const figura = catalogo.figuras.find((candidata) => candidata.id === id);
  if (!figura) return null;

  return {
    icon: figura.id,
    draw: [...figura.formas],
    grid: catalogo.grade,
    outline: catalogo.contorno,
    x: POSICAO_INICIAL.x,
    y: POSICAO_INICIAL.y,
    size: TAMANHO_PADRAO,
    rotation: 0,
    color: cor,
  };
}
